import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import type { settingTypes } from '@ipfabric/utils';
import { FormikSwitchField } from 'components/FormikSwitchField/FormikSwitchField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

type AuthenticationType = 'login' | 'apiToken';

export type CiscoFmcFormValues =
  BaseVendorFormValues<settingTypes.CiscoFmcApiSettings> &
    settingTypes.UsernamePasswordAuthentication &
    settingTypes.ApiTokenAuthentication & {
      authentication?: AuthenticationType;
    };

const resetSecretAttributes = (
  values: CiscoFmcFormValues,
): CiscoFmcFormValues => {
  return {
    ...values,
    apiToken: '',
    username: '',
    password: '',
  };
};

type Props = VendorDialogProps<
  CiscoFmcFormValues,
  settingTypes.CiscoFmcApiSettings,
  settingTypes.UpdateCiscoFmcApiSettings
>;

const BaseCiscoFmcForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const isFmcTokenEnabled = isFeatureFlagEnabled('ENABLE_FMC_TOKEN_AUTH');
  const { validation, token, authentication } =
    msg.discoverySettings.vendorsApi.ciscoFmcForm;
  const formik = useFormik<CiscoFmcFormValues>({
    initialValues: resetSecretAttributes({
      ...props.initialValues,
      authentication: 'login',
    }),
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable for validation
    validate: (values) => {
      const { required, baseUrlStartsWith } = validation;
      const errors: FormikErrors<CiscoFmcFormValues> = {};

      if (!values.baseUrl) {
        errors.baseUrl = required;
      } else if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = baseUrlStartsWith;
      }

      if (values.authentication === 'login') {
        if (!editMode && !values.username) {
          errors.username = required;
        }
        if (!editMode && !values.password) {
          errors.password = required;
        }
      }
      if (values.authentication === 'apiToken') {
        if (!editMode && !values.apiToken) {
          errors.apiToken = required;
        }
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit(
      ({ authentication, ...values }: CiscoFmcFormValues) => {
        const { username, password, apiToken, ...formattedValues } = {
          ...values,
          ...getAdvancedSettingsDefaultValues(values),
        };

        if (isVendorUpdate(values) && props.dialogType === 'edit') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              username: username || formik.initialValues.username || undefined,
              password: password || formik.initialValues.password || undefined,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              apiToken: apiToken || formik.initialValues.apiToken || undefined,
            });
          }
        } else if (props.dialogType === 'add') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              username,
              password,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              apiToken,
            });
          }
        }
      },
    ),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<CiscoFmcFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      {isFmcTokenEnabled ? (
        <FormikSwitchField
          name='authentication'
          label={authentication}
          formik={formik}
          options={[
            {
              label: 'Username / Password',
              value: 'login',
            },
            {
              label: 'API Token',
              value: 'apiToken',
            },
          ]}
          size='small'
        />
      ) : null}
      {formik.values.authentication === 'login' && (
        <FormikVendorApiUsernamePasswordFields
          formik={formik}
          isEditMode={editMode}
        />
      )}
      {formik.values.authentication === 'apiToken' && (
        <FormikTextField
          formik={formik}
          name='apiToken'
          label={token}
          placeholder={editMode ? '(unchanged)' : undefined}
          type='password'
        />
      )}
      <FormikVendorApiBaseUrlField formik={formik} placeholder='https://fmc' />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const CiscoFmcForm = memo(BaseCiscoFmcForm);
